import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PhoneIncoming, PhoneOutgoing, ArrowLeftRight, Pencil, ChevronDown,
  UserCheck, Save, Trash2, Download,
} from "lucide-react";
import { toast } from "sonner";
import { fetchContact, updateContact, deleteContact, recordingUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/CallList";
import { AudioPlayer } from "@/components/AudioPlayer";
import { CallButton, WhatsAppButton, CopyNumberButton } from "@/components/CallFlow";
import { LineBadge } from "@/components/LineBadge";
import { CallAiSection } from "@/components/CallAiSection";
import { formatCallTime, formatCallDuration, initials } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const DIR_ICON = { inbound: PhoneIncoming, outbound: PhoneOutgoing, internal: ArrowLeftRight };
const DIR_COLOR = { inbound: "text-indigo-500", outbound: "text-blue-500", internal: "text-slate-400" };
const DIR_LABEL = { inbound: "Entrant", outbound: "Sortant", internal: "Interne" };

// One history entry: header (direction, status, time, duration pill, answered by)
// expands inline to show the recording player + AI placeholders.
const HistoryItem = ({ call, expanded, onToggle }) => {
  const { user } = useAuth();
  const isAgent = user?.role === "agent";
  const DirIcon = DIR_ICON[call.direction] || ArrowLeftRight;
  const dur = formatCallDuration(call);
  const [seekSignal, setSeekSignal] = useState(null);
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        data-testid={`history-item-${call.id}`}
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full text-left p-3.5 flex items-center gap-3 hover:bg-slate-50 active:bg-slate-100 transition-colors"
      >
        <span className={`h-9 w-9 rounded-full border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0 ${DIR_COLOR[call.direction] || "text-slate-400"}`}>
          <DirIcon className="h-4 w-4" strokeWidth={2.4} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-900">{DIR_LABEL[call.direction] || "Interne"}</span>
            <div className="flex items-center gap-1.5">
            <StatusBadge status={call.status} />
            <LineBadge line={call.line} compact testid={`line-badge-call-${call.id}`} />
          </div>
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-xs text-slate-500 tabular-nums">{formatCallTime(call.started_at)}</span>
            {dur !== "—" && (
              <span data-testid={`duration-pill-${call.id}`} className="rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[11px] font-semibold tabular-nums">
                {dur}
              </span>
            )}
            {call.answered_by && (
              <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                <UserCheck className="h-3 w-3 text-emerald-600" />
                {call.direction === "outbound" ? "Appelé par" : "Répondu par"} {call.answered_by}
              </span>
            )}
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="border-t border-slate-100 p-4 space-y-4 bg-slate-50/40" data-testid={`history-detail-${call.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <WhatsAppButton number={call.number} line={call.line} compact testid={`whatsapp-btn-${call.id}`} />
            <CopyNumberButton number={call.number} compact testid={`copy-number-btn-${call.id}`} />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-xs tracking-[0.05em] uppercase font-semibold text-slate-500">Enregistrement</p>
              {call.has_recording && !isAgent && (
                <a
                  data-testid={`download-recording-${call.id}`}
                  href={`${recordingUrl(call.id)}?download=1`}
                  download
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-700 active:bg-emerald-50 transition-colors whitespace-nowrap"
                >
                  <Download className="h-3.5 w-3.5" /> Télécharger
                </a>
              )}
            </div>
            {call.has_recording ? (
              <AudioPlayer
                src={recordingUrl(call.id)}
                duration={call.recording_duration_seconds}
                seekSignal={seekSignal}
              />
            ) : (
              <>
                <AudioPlayer disabled />
                <p className="text-xs text-slate-400 mt-2">Aucun enregistrement disponible pour cet appel.</p>
              </>
            )}
          </div>
          <CallAiSection
            callId={call.id}
            hasRecording={call.has_recording}
            onSeek={(t) => setSeekSignal({ t, n: Date.now() })}
          />
        </div>
      )}
    </div>
  );
};

// Unified "number" view: same screen whether opened from the calls list or from
// Contacts. Header (name/number/company/note + actions) + full call history.
export const NumberView = ({ numberKey, onDeleted }) => {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAgent = user?.role === "agent";
  const { data, isLoading } = useQuery({
    queryKey: ["contact", numberKey],
    queryFn: () => fetchContact(numberKey),
    enabled: !!numberKey,
  });
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", note: "" });
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!data) return;
    setForm({ name: data.has_name ? data.name : "", company: data.company || "", note: data.note || "" });
    setExpandedId((prev) => (prev && data.calls.some((c) => c.id === prev) ? prev : data.calls[0]?.id ?? null));
  }, [data]);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["contact", numberKey] });
    qc.invalidateQueries({ queryKey: ["contacts"] });
    qc.invalidateQueries({ queryKey: ["calls-grouped"] });
    qc.invalidateQueries({ queryKey: ["calls"] });
  };
  const saveMut = useMutation({
    mutationFn: () => updateContact(numberKey, form),
    onSuccess: () => { invalidateAll(); toast.success("Contact enregistré"); setEditOpen(false); },
    onError: () => toast.error("Échec de l'enregistrement"),
  });
  const delMut = useMutation({
    mutationFn: () => deleteContact(numberKey),
    onSuccess: () => { invalidateAll(); toast.success("Contact supprimé"); setEditOpen(false); onDeleted?.(); },
    onError: () => toast.error("Échec de la suppression"),
  });

  if (isLoading || !data) {
    return <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">Chargement…</div>;
  }

  return (
    <div data-testid="number-view" className="space-y-5">
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
        <div className="flex items-center gap-3">
          <span className="h-12 w-12 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-sm font-semibold text-slate-500 shrink-0">
            {initials(data.name)}
          </span>
          <div className="min-w-0">
            <h2 data-testid="number-view-name" className="font-heading font-bold text-slate-900 text-lg truncate">{data.name}</h2>
            <p data-testid="number-view-number" className="text-sm text-slate-400 tabular-nums">{data.number}</p>
            {data.company && <p data-testid="number-view-company" className="text-sm text-slate-500 mt-0.5 truncate">{data.company}</p>}
            {data.note && <p data-testid="number-view-note" className="text-xs text-slate-400 mt-0.5">{data.note}</p>}
          </div>
        </div>
        <div className="flex flex-wrap items-start gap-2 mt-4">
          <CallButton
            numberKey={data.number_key}
            number={data.number}
            name={data.has_name ? data.name : null}
            defaultLine={data.calls?.[0]?.line}
          />
          <WhatsAppButton number={data.number} line={data.calls?.[0]?.line} testid="whatsapp-btn-header" />
          <CopyNumberButton number={data.number} testid="copy-number-btn-header" />
          <button
            type="button"
            data-testid="edit-contact-btn"
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-medium px-4 py-2.5 hover:border-emerald-300 hover:text-emerald-700 transition-colors"
          >
            <Pencil className="h-4 w-4" /> Modifier le contact
          </button>
        </div>
      </div>

      <div>
        <h3 className="font-heading font-semibold text-slate-900 text-base mb-3">
          Historique des appels {data.calls.length > 0 && <span className="text-slate-400 font-normal">({data.calls.length})</span>}
        </h3>
        {data.calls.length ? (
          <div className="space-y-2" data-testid="number-history">
            {data.calls.map((c) => (
              <HistoryItem
                key={c.id}
                call={c}
                expanded={expandedId === c.id}
                onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-400">Aucun appel avec ce numéro.</div>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier le contact</DialogTitle>
            <DialogDescription className="sr-only">Modifier le nom, la société et la note du contact</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Nom</Label>
              <Input data-testid="contact-edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="Ex : Daphne Ghenassia" />
            </div>
            <div>
              <Label className="text-sm">Société</Label>
              <Input data-testid="contact-edit-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-sm">Note</Label>
              <Textarea data-testid="contact-edit-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} className="mt-1" placeholder="Client — Clio 4, pneus 205/55 R16" />
            </div>
          </div>
          <DialogFooter className={`flex-row gap-2 ${isAgent ? "justify-end sm:justify-end" : "justify-between sm:justify-between"}`}>
            {!isAgent && (
              <button
                type="button"
                data-testid="delete-contact-btn"
                onClick={() => delMut.mutate()}
                disabled={delMut.isPending}
                className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" /> Supprimer
              </button>
            )}
            <button
              type="button"
              data-testid="save-contact-detail-btn"
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold px-5 py-2.5 hover:bg-emerald-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" /> Enregistrer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
